import React, { useState, useMemo } from 'react'
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Stack,
} from '@mui/material'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SentimentSatisfiedIcon from '@mui/icons-material/SentimentSatisfied'
import SentimentNeutralIcon from '@mui/icons-material/SentimentNeutral'
import SentimentDissatisfiedIcon from '@mui/icons-material/SentimentDissatisfied'
import type { AutoCoderPipelineRecord, AutoCoderFramework } from '../../types/gotypes'
import ExpandedMatchDetailsModal from '../autocoder/ExpandedMatchDetailsModal'

interface SocialCommentTableProps {
  data: AutoCoderPipelineRecord[]
  totalCount: number
  page: number
  rowsPerPage: number
  frameworksMap?: Record<string, AutoCoderFramework[]>
  colorMap?: Record<string, string>
  onPageChange: (page: number) => void
  onRowsPerPageChange: (rowsPerPage: number) => void
}

// Theme/code colors
const THEME_COLORS: Record<string, string> = {
  positive: '#4caf50',
  negative: '#f44336',
  neutral: '#ff9800',
  joy: '#4caf50',
  trust: '#2196f3',
  surprise: '#ff9800',
  anticipation: '#9c27b0',
  anger: '#f44336',
  sadness: '#607d8b',
  disgust: '#795548',
  fear: '#e91e63',
}

/**
 * Get color for a theme/code label
 */
function getThemeColor(label: string, colorMap?: Record<string, string>): string {
  const normalizedLabel = label.toLowerCase()

  // Check custom color map first
  if (colorMap?.[normalizedLabel]) {
    return colorMap[normalizedLabel]
  }

  // Check built-in theme colors
  if (THEME_COLORS[normalizedLabel]) {
    return THEME_COLORS[normalizedLabel]
  }

  // Default color
  return '#6366f1'
}

/**
 * Render sentiment icon with filled background
 */
function SentimentIcon({ sentiment }: { sentiment: string }) {
  const s = sentiment?.toLowerCase() || 'neutral'

  const getConfig = () => {
    switch (s) {
      case 'positive':
        return { icon: SentimentSatisfiedIcon, color: '#22C55E', bg: 'rgba(34, 197, 94, 0.15)' }
      case 'negative':
        return { icon: SentimentDissatisfiedIcon, color: '#EF4444', bg: 'rgba(239, 68, 68, 0.15)' }
      default:
        return { icon: SentimentNeutralIcon, color: '#94A3B8', bg: 'rgba(148, 163, 184, 0.15)' }
    }
  }

  const config = getConfig()
  const IconComponent = config.icon

  return (
    <Box
      sx={{
        width: 28,
        height: 28,
        borderRadius: '50%',
        bgcolor: config.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <IconComponent sx={{ color: config.color, fontSize: 18 }} />
    </Box>
  )
}

/**
 * Extract coded themes from a record's matches
 */
function extractThemes(record: AutoCoderPipelineRecord, frameworksMap?: Record<string, AutoCoderFramework[]>): string[] {
  const data = record.data
  if (!data?.matches?.length) return []

  const frameworks = frameworksMap?.[record.auto_coder_pipeline_id] || []

  // Build a map of code -> label from frameworks
  const labelMap: Record<string, Record<string, string>> = {}
  frameworks.forEach(fw => {
    if (fw.main_variable?.name) {
      labelMap[fw.main_variable.name] = {}
      fw.main_variable.codes?.forEach(code => {
        if (code.code) {
          labelMap[fw.main_variable.name][code.code] = code.text || code.code
        }
      })
    }
  })

  // Extract unique theme labels from matches
  const themes = new Set<string>()
  data.matches.forEach(match => {
    if (match.root_match) {
      const varName = match.root_match.name || ''
      const code = match.root_match.code || ''
      const label = labelMap[varName]?.[code] || code
      if (label) themes.add(label)
    }
  })

  return Array.from(themes)
}

/**
 * SocialCommentTable Component
 *
 * Displays social media comments with their coded themes.
 * Shows: ID, Comment text, Coded themes, Sentiment (if available), Details
 */
export const SocialCommentTable: React.FC<SocialCommentTableProps> = ({
  data,
  totalCount,
  page,
  rowsPerPage,
  frameworksMap,
  colorMap,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const [expandedRecordId, setExpandedRecordId] = useState<string | null>(null)

  const handleViewDetails = (recordId: string) => {
    setExpandedRecordId(recordId)
  }

  const handleCloseDetails = () => {
    setExpandedRecordId(null)
  }

  // Process records to extract display data
  const processedData = useMemo(() => {
    return data.map((record) => {
      const metadata = record.metadata || {}
      const themes = extractThemes(record, frameworksMap)

      // Find all sentiments from themes or metadata
      const sentimentValues = ['positive', 'negative', 'neutral']
      const sentimentsFromThemes = themes.filter(t =>
        sentimentValues.includes(t.toLowerCase())
      ).map(t => t.toLowerCase())

      // Also check metadata for sentiment (could be comma-separated)
      const metadataSentiment = metadata['sentiment'] || metadata['Sentiment'] || ''
      const sentimentsFromMetadata = metadataSentiment
        ? metadataSentiment.split(',').map((s: string) => s.trim().toLowerCase()).filter((s: string) => sentimentValues.includes(s))
        : []

      // Combine and dedupe
      const sentiments = [...new Set([...sentimentsFromThemes, ...sentimentsFromMetadata])]

      return {
        record,
        id: record.interview_id || record.id,
        text: record.data?.source_text || '',
        themes,
        sentiments,
        metadata,
      }
    })
  }, [data, frameworksMap])

  const getFrameworksForRecord = (record: AutoCoderPipelineRecord): AutoCoderFramework[] => {
    if (frameworksMap && record.auto_coder_pipeline_id) {
      return frameworksMap[record.auto_coder_pipeline_id] || []
    }
    return []
  }

  const expandedRecord = data.find((record) => record.id === expandedRecordId) || null
  const expandedRecordFrameworks = expandedRecord ? getFrameworksForRecord(expandedRecord) : []

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '50%' }}>Comment</TableCell>
                <TableCell sx={{ width: '30%' }}>Tags</TableCell>
                <TableCell align="center" sx={{ width: 80 }}>Sentiment</TableCell>
                <TableCell align="center" sx={{ width: 60 }}>Details</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No comments available.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                processedData.map((item) => (
                  <TableRow key={item.record.id} hover>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.5,
                        }}
                      >
                        {item.text}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ gap: 0.5 }}>
                        {item.themes.slice(0, 5).map((theme, idx) => (
                          <Chip
                            key={idx}
                            label={theme}
                            size="small"
                            sx={{
                              bgcolor: getThemeColor(theme, colorMap),
                              color: 'white',
                              fontWeight: 500,
                              fontSize: '0.65rem',
                              height: 20,
                            }}
                          />
                        ))}
                        {item.themes.length > 5 && (
                          <Tooltip title={item.themes.slice(5).join(', ')}>
                            <Chip
                              label={`+${item.themes.length - 5}`}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.65rem', height: 20 }}
                            />
                          </Tooltip>
                        )}
                        {item.themes.length === 0 && (
                          <Typography variant="caption" color="text.secondary">
                            No tags
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      {item.sentiments.length > 0 ? (
                        <Tooltip title={item.sentiments.join(', ')}>
                          <Stack direction="row" spacing={0.5} justifyContent="center">
                            {item.sentiments.map((s, idx) => (
                              <SentimentIcon key={idx} sentiment={s} />
                            ))}
                          </Stack>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleViewDetails(item.record.id)}
                        title="View details"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount ?? data.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(e, newPage) => onPageChange(newPage)}
          onRowsPerPageChange={(e) => onRowsPerPageChange(Number(e.target.value))}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Box>
      <ExpandedMatchDetailsModal
        open={expandedRecordId !== null}
        record={expandedRecord}
        frameworks={expandedRecordFrameworks}
        colorMap={colorMap}
        onClose={handleCloseDetails}
      />
    </>
  )
}

export default SocialCommentTable
